import React, { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, BookOpen, Heart } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { BOOKING_URL } from '@/constants/urls';
import LegalServiceSchema from '@/components/LegalServiceSchema';

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Women and The Way - Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Privacy Policy for Women and The Way Chinese Medicine clinic in Currimundi, Sunshine Coast. How we collect, use, and protect your personal information.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Privacy Policy | Women and The Way");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Privacy Policy for Women and The Way Chinese Medicine clinic. How we collect, use, and protect your personal information.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/privacy-policy");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Privacy Policy | Women and The Way");
    }
    return () => {
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  const handleBooking = () => {
    window.open(BOOKING_URL, '_blank');
  };

  return (
    <>
      <LegalServiceSchema
        pageType="privacy"
        title="Privacy Policy | Women and The Way"
        description="Privacy Policy for Women and The Way Chinese Medicine clinic in Currimundi, Sunshine Coast. How we collect, use, and protect your personal information."
        url="https://womenandtheway.com.au/privacy-policy"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section */}
        <section className="py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="mb-8">
              <Heart className="w-12 h-12 text-sage-green mx-auto mb-6" />
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-earth-brown mb-6 leading-tight">
                Privacy Policy
              </h1>
              <p className="text-xl text-gray-600 font-light">
                Your privacy is held with care and intention at every step of your journey
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-4xl">
            {/* Introduction */}
            <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
              <CardContent className="p-8">
                <h2 className="font-serif text-2xl font-bold mb-6 text-earth-brown">Your Privacy Matters</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    At Women and the Way, we honour your story. In every form you complete, in every conversation shared, and in every treatment received  -  your privacy is held with care.
                  </p>
                  <p>
                    This Privacy Policy outlines how we collect, store, use, and protect your personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Policy Sections */}
            <div className="space-y-8">
              {/* 1. Collection of Personal Information */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">📋</span>
                    1. Collection of Personal Information
                  </h3>
                  <p className="text-gray-700 mb-4">
                    We collect information necessary to provide safe and appropriate care. This may include:
                  </p>
                  <ul className="space-y-2 text-gray-700 ml-4">
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Name, date of birth, contact details</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Health history, medications, symptoms, and relevant pathology</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Appointment notes and practitioner observations</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Payment and billing details</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    We may collect information from you directly or from authorised third parties (e.g., referrals) with your consent.
                  </p>
                </CardContent>
              </Card>

              {/* 2. Use of Personal Information */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💡</span>
                    2. Use of Personal Information
                  </h3>
                  <p className="text-gray-700">
                    We use your information:
                  </p>
                  <ul className="space-y-2 text-gray-700 ml-4">
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>To provide treatments and health services</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>To communicate with you about appointments or your care</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>To send practice-related updates (with your permission)</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>To comply with legal and regulatory obligations</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    We do not use your information for marketing unless you have opted in.
                  </p>
                </CardContent>
              </Card>

              {/* 3. Storage and Security */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🔒</span>
                    3. Storage and Security
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Your records are stored securely  -  both digitally and physically  -  with safeguards to prevent misuse, loss, or unauthorised access. Our systems are password-protected and encrypted.
                  </p>
                  <p className="text-gray-700">
                    Only authorised staff and practitioners can access your records, and all are bound by confidentiality agreements.
                  </p>
                </CardContent>
              </Card>

              {/* 4. Disclosure */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🔗</span>
                    4. Disclosure
                  </h3>
                  <p className="text-gray-700 mb-4">
                    We will never sell or share your data for profit. We may disclose your information:
                  </p>
                  <ul className="space-y-2 text-gray-700 ml-4">
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>With your consent</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>To another healthcare provider when coordinating your care</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>If legally required (e.g., subpoena or mandatory reporting)</li>
                  </ul>
                </CardContent>
              </Card>

              {/* 5. Access and Corrections */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">✏️</span>
                    5. Access and Corrections
                  </h3>
                  <p className="text-gray-700">
                    You have the right to request access to your records or correct inaccurate details. Please email us at <a href="mailto:womenandtheway@gmail.com" className="text-sage-green underline">womenandtheway@gmail.com</a> or speak with your practitioner. We will respond within a reasonable timeframe.
                  </p>
                </CardContent>
              </Card>

              {/* 6. Third Parties and Online Tools */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🌐</span>
                    6. Third Parties and Online Tools
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Our website may use cookies or third-party services (e.g., booking tools, payment platforms) that collect anonymous usage data. These tools help us improve the experience but do not store your private health information.
                  </p>
                  <p className="text-gray-700">
                    If you use third-party platforms (e.g., Instagram or Stripe), their privacy policies also apply.
                  </p>
                </CardContent>
              </Card>

              {/* 7. Retention */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🗄️</span>
                    7. Retention
                  </h3>
                  <p className="text-gray-700">
                    We retain records for the period required by law (usually 7 years or until a child turns 25) and securely dispose of data when no longer needed.
                  </p>
                </CardContent>
              </Card>

              {/* 8. Concerns or Complaints */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">📞</span>
                    8. Concerns or Complaints
                  </h3>
                  <p className="text-gray-700 mb-4">
                    We take privacy concerns seriously. If you have a complaint, please contact us directly. If unresolved, you can contact the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" className="text-sage-green underline" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a>.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Closing Section */}
            <Card className="bg-sage-800 text-white border-2 border-amber-200/50 shadow-sm mt-8 rounded-2xl">
              <CardContent className="p-8">
                <h3 className="font-serif text-2xl font-bold mb-6">With Deep Respect</h3>
                <p className="text-sage-100 leading-relaxed mb-4">
                  We hold your information  -  like your healing  -  with intention and care. Thank you for trusting us with both.
                </p>
                <div className="text-center mt-6">
                  <p className="text-sage-200 font-medium">Women and the Way</p>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Button 
                size="lg" 
                className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                onClick={handleBooking}
              >
                Book Your Consultation
                <Shield className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PrivacyPolicyPage; 